/**
 * Staff Quick Sale API (Story 8-4).
 * Create on-the-spot delivered orders and list today's quick sales.
 */
import api from "./api";
import type { OrderDiscountType, OrderDiscountScope } from "../types/models";

/** Story 10.5-3: id is catalog_item_id (from list_products_for_portal). */
/** Story 10.5-13: sales_pack_sizes for Units/Packs and quick-add by pack. */
export interface StaffProductItem {
  id: string;
  name: string;
  sku?: string;
  category?: string;
  item_type?: string;
  unit_of_measure?: string;
  unit?: string;
  description?: string;
  unit_price: number;
  quantity?: number;
  availability_status?: string;
  sales_pack_sizes?: number[] | null;
  primary_photo_id?: string | null; // Story 10.7-3: attachment id of primary photo
}

export interface StaffProductListResponse {
  items: StaffProductItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/** Story 10.5-3: catalog-driven; id from list_products_for_portal is catalog_item_id. 11.4-1: price+discount. */
export interface QuickSaleItemCreate {
  catalog_item_id: string;
  quantity: number;
  /** Story 11.4-1: custom per-unit price snapshot (omit to use catalog price). */
  unit_price?: number;
  discount_type?: OrderDiscountType;
  discount_value?: number;
}

export interface QuickSaleCreatePayload {
  customer_id: string | null;
  items: QuickSaleItemCreate[];
  notes?: string | null;
  /** When true, allow sale when quantity > available (AC6 physical count discrepancy). Default false. */
  allow_negative_stock?: boolean;
  /** Story 11.4-1: 'none' (default) | 'line' | 'order'. */
  discount_scope?: OrderDiscountScope;
  discount_type?: OrderDiscountType;
  discount_value?: number;
  discount_reason?: string | null;
  /** Story 11.6-1: opt-in deferred payment (requires a real customer). */
  deferred?: boolean;
  payment_due_date?: string | null;
}

export interface QuickSaleItemResponse {
  id: string;
  order_id: string;
  catalog_item_id?: string | null;
  inventory_item_id: string;
  quantity: number;
  unit_price: number;
  discount_type?: OrderDiscountType | null;
  discount_value?: number | null;
  discount_amount?: number;
  line_total: number;
  product_name: string;
}

export interface QuickSaleResponse {
  id: string;
  order_number: string;
  subtotal?: number;
  total_amount: number;
  discount_scope?: OrderDiscountScope;
  discount_type?: OrderDiscountType | null;
  discount_value?: number | null;
  discount_amount?: number;
  discount_reason?: string | null;
  items: QuickSaleItemResponse[];
}

export interface QuickSaleListItem {
  id: string;
  order_number: string;
  customer_name: string;
  subtotal?: number;
  total_amount: number;
  discount_scope?: OrderDiscountScope;
  discount_type?: OrderDiscountType | null;
  discount_value?: number | null;
  discount_amount?: number;
  discount_reason?: string | null;
  created_at: string;
  items: QuickSaleItemResponse[];
}

export interface QuickSaleListResponse {
  items: QuickSaleListItem[];
}

/** List sellable products for quick sale form (GET /api/staff/products). */
export async function listStaffProducts(params?: {
  page?: number;
  limit?: number;
  search?: string | null;
  include_out_of_stock?: boolean;
}): Promise<StaffProductListResponse> {
  const { page = 1, limit = 100, search, include_out_of_stock = true } = params ?? {};
  const response = await api.get<StaffProductListResponse>("/staff/products", {
    params: { page, limit, search: search ?? undefined, include_out_of_stock },
  });
  return response.data;
}

/** Create a quick sale (POST /api/staff/quick-sale). customer_id null = walk-in. */
export async function createQuickSale(payload: QuickSaleCreatePayload): Promise<QuickSaleResponse> {
  const response = await api.post<QuickSaleResponse>("/staff/quick-sale", payload);
  return response.data;
}

/** List current staff's quick sales for today (GET /api/staff/quick-sales). */
export async function listMyQuickSalesToday(limit = 50): Promise<QuickSaleListResponse> {
  const response = await api.get<QuickSaleListResponse>("/staff/quick-sales", {
    params: { limit },
  });
  return response.data;
}
